import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { UploadCloud, GraduationCap, IndianRupee, FileText, User2, Building2, Check } from "lucide-react";
import api from "@/lib/api";
import { PublicHeader, PublicFooter } from "@/components/PublicChrome";
import WhatsAppButton, { WhatsAppInfo } from "@/components/WhatsAppButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MAX_SIZE = 5 * 1024 * 1024;
const IMG = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const PDF_OR_IMG = [...IMG, "application/pdf"];

function FileField({ id, label, accept, hint, onFile, previewUrl, testId, required = true, isPdf }) {
  const inputRef = useRef();
  return (
    <div className="space-y-2">
      <Label className="tech-label" htmlFor={id}>{label}{required && " *"}</Label>
      <div
        onClick={() => inputRef.current?.click()}
        className="cursor-pointer border border-dashed border-zinc-300 rounded-lg bg-zinc-50 hover:bg-zinc-100 transition-colors p-6 flex items-center gap-4"
        data-testid={testId}
      >
        {previewUrl && !isPdf ? (
          <img src={previewUrl} alt="preview" className="w-16 h-16 object-cover rounded-md border border-zinc-200" />
        ) : previewUrl && isPdf ? (
          <div className="w-16 h-16 rounded-md bg-white border border-zinc-200 flex items-center justify-center">
            <FileText size={22} className="text-red-600" />
          </div>
        ) : (
          <div className="w-16 h-16 rounded-md bg-white border border-zinc-200 flex items-center justify-center">
            <UploadCloud size={22} className="text-zinc-500" />
          </div>
        )}
        <div className="flex-1">
          <p className="text-sm font-semibold text-zinc-900">{previewUrl ? "File selected — click to change" : "Click to upload"}</p>
          <p className="text-xs text-zinc-500 mt-0.5">{hint}</p>
        </div>
      </div>
      <input
        id={id}
        ref={inputRef}
        type="file"
        className="hidden"
        accept={accept}
        onChange={(e) => onFile(e.target.files?.[0] || null)}
      />
    </div>
  );
}

export default function ApplicationForm() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [files, setFiles] = useState({ photo: null, signature: null, pan: null, aadhaar: null, bc_csp: null });
  const [previews, setPreviews] = useState({});

  const { register, handleSubmit, control, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      training_status: "with_training",
      has_training_id: "no",
      is_bc_csp: "no",
      gender: "male",
      declaration: false,
    },
  });

  const trainingStatus = watch("training_status");
  const hasTrainingId = watch("has_training_id");
  const isBcCsp = watch("is_bc_csp");
  const declaration = watch("declaration");

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const fee = trainingStatus === "with_training" ? (config?.fee_with_training ?? 1500) : (config?.fee_without_training ?? 5000);

  const setFile = (key, allowed) => (f) => {
    if (!f) { setFiles((x) => ({ ...x, [key]: null })); setPreviews((p) => { const c = { ...p }; delete c[key]; return c; }); return; }
    if (!allowed.includes(f.type)) { toast.error(`Invalid type for ${key}: ${f.type}`); return; }
    if (f.size > MAX_SIZE) { toast.error(`${key}: file must be ≤ 5MB`); return; }
    setFiles((x) => ({ ...x, [key]: f }));
    const url = URL.createObjectURL(f);
    setPreviews((p) => ({ ...p, [key]: { url, isPdf: f.type === "application/pdf" } }));
  };

  const onSubmit = async (data) => {
    if (!data.declaration) { toast.error("Please accept the declaration"); return; }
    if (!files.photo || !files.signature || !files.pan || !files.aadhaar) {
      toast.error("Please upload all required documents"); return;
    }
    if (data.is_bc_csp === "yes" && !files.bc_csp) { toast.error("Upload BC/CSP proof"); return; }

    const fd = new FormData();
    Object.entries(data).forEach(([k, v]) => {
      if (k === "declaration") fd.append(k, v ? "true" : "false");
      else if (v !== undefined && v !== null) fd.append(k, v);
    });
    fd.append("photo", files.photo);
    fd.append("signature", files.signature);
    fd.append("pan_doc", files.pan);
    fd.append("aadhaar_doc", files.aadhaar);
    if (files.bc_csp) fd.append("bc_csp_doc", files.bc_csp);

    setSubmitting(true);
    try {
      const { data: res } = await api.post("/applications", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Application submitted: ${res.application_no}`);
      navigate(`/success/${res.application_no}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="mb-10">
          <p className="tech-label">IIBF · BCBF · 2026</p>
          <h1 className="mt-2 text-4xl sm:text-5xl lg:text-6xl font-display font-black tracking-tighter">Business Correspondent / <br/>Business Facilitator Registration.</h1>
          <p className="mt-4 text-base text-zinc-600 max-w-2xl leading-relaxed">
            Register for the BCBF certification. Complete the form, upload your documents, and receive your application number instantly.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-10" data-testid="bcbf-application-form">
          {/* 1. Training Selection */}
          <section className="border border-zinc-200 rounded-lg p-8 bg-white">
            <div className="flex items-center gap-2 mb-1"><GraduationCap size={16} className="text-zinc-700"/><p className="tech-label">Step 01 — Application Type</p></div>
            <h2 className="font-display text-2xl font-bold tracking-tight mb-6">Choose your training status</h2>
            <Controller
              control={control}
              name="training_status"
              render={({ field }) => (
                <RadioGroup value={field.value} onValueChange={field.onChange} className="grid sm:grid-cols-2 gap-4">
                  {[
                    { v: "with_training", label: "With Training", fee: config?.fee_with_training ?? 1500, desc: "You already completed BCBF training" },
                    { v: "without_training", label: "Without Training", fee: config?.fee_without_training ?? 5000, desc: "Training will be provided as part of registration" },
                  ].map((o) => (
                    <label key={o.v} htmlFor={`ts-${o.v}`} data-testid={`training-option-${o.v}`}
                      className={`cursor-pointer rounded-md border p-5 transition-all ${field.value === o.v ? "border-zinc-900 ring-1 ring-zinc-900 bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <RadioGroupItem id={`ts-${o.v}`} value={o.v} />
                            <span className="font-semibold text-zinc-900">{o.label}</span>
                          </div>
                          <p className="mt-2 text-xs text-zinc-500">{o.desc}</p>
                        </div>
                        <div className="text-right">
                          <p className="tech-label">Fee</p>
                          <p className="font-display text-2xl font-black tracking-tight flex items-center"><IndianRupee size={18} strokeWidth={3}/>{o.fee.toLocaleString("en-IN")}</p>
                        </div>
                      </div>
                    </label>
                  ))}
                </RadioGroup>
              )}
            />
            <div className="mt-6 flex items-center justify-between rounded-md bg-zinc-900 text-white px-5 py-4">
              <div>
                <p className="tech-label text-zinc-400">Payable Amount (Server-verified)</p>
                <p className="font-display text-3xl font-black tracking-tighter flex items-center" data-testid="displayed-fee"><IndianRupee size={22} strokeWidth={3}/>{fee.toLocaleString("en-IN")}</p>
              </div>
              <div className="text-right">
                <p className="tech-label text-zinc-400">Payment</p>
                <p className="text-sm font-semibold">Pending / Offline</p>
              </div>
            </div>
          </section>

          {/* 2. Personal details */}
          <section className="border border-zinc-200 rounded-lg p-8 bg-white">
            <div className="flex items-center gap-2 mb-1"><User2 size={16} className="text-zinc-700"/><p className="tech-label">Step 02 — Personal Details</p></div>
            <h2 className="font-display text-2xl font-bold tracking-tight mb-6">About you</h2>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="tech-label">Full Name *</Label>
                <Input className="h-12 bg-zinc-50 mt-2" data-testid="input-full-name" {...register("full_name", { required: true, minLength: 2 })} />
                {errors.full_name && <p className="text-xs text-red-600 mt-1">Required</p>}
              </div>
              <div>
                <Label className="tech-label">Father's Name *</Label>
                <Input className="h-12 bg-zinc-50 mt-2" data-testid="input-father-name" {...register("father_name", { required: true })} />
              </div>
              <div>
                <Label className="tech-label">Date of Birth *</Label>
                <Input type="date" className="h-12 bg-zinc-50 mt-2" data-testid="input-dob" {...register("dob", { required: true })} />
              </div>
              <div>
                <Label className="tech-label">Gender *</Label>
                <Controller
                  control={control} name="gender"
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="h-12 bg-zinc-50 mt-2" data-testid="input-gender"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <div>
                <Label className="tech-label">Mobile (10 digits) *</Label>
                <Input inputMode="numeric" maxLength={10} className="h-12 bg-zinc-50 mt-2" data-testid="input-mobile" {...register("mobile", { required: true, pattern: /^\d{10}$/ })} />
                {errors.mobile && <p className="text-xs text-red-600 mt-1">10 digits required</p>}
              </div>
              <div>
                <Label className="tech-label">Email *</Label>
                <Input type="email" className="h-12 bg-zinc-50 mt-2" data-testid="input-email" {...register("email", { required: true })} />
              </div>
              <div className="sm:col-span-2">
                <Label className="tech-label">Address *</Label>
                <Textarea rows={2} className="bg-zinc-50 mt-2" data-testid="input-address" {...register("address", { required: true })} />
              </div>
              <div>
                <Label className="tech-label">State *</Label>
                <Input className="h-12 bg-zinc-50 mt-2" data-testid="input-state" {...register("state", { required: true })} />
              </div>
              <div>
                <Label className="tech-label">District *</Label>
                <Input className="h-12 bg-zinc-50 mt-2" data-testid="input-district" {...register("district", { required: true })} />
              </div>
              <div>
                <Label className="tech-label">Pincode (6 digits) *</Label>
                <Input inputMode="numeric" maxLength={6} className="h-12 bg-zinc-50 mt-2" data-testid="input-pincode" {...register("pincode", { required: true, pattern: /^\d{6}$/ })} />
              </div>
              <div>
                <Label className="tech-label">Education *</Label>
                <Input className="h-12 bg-zinc-50 mt-2" data-testid="input-education" {...register("education", { required: true })} />
              </div>
              <div>
                <Label className="tech-label">PAN (AAAAA9999A) *</Label>
                <Input maxLength={10} className="h-12 bg-zinc-50 mt-2 uppercase" data-testid="input-pan" {...register("pan", { required: true, pattern: /^[A-Za-z]{5}[0-9]{4}[A-Za-z]$/ })} />
                {errors.pan && <p className="text-xs text-red-600 mt-1">Invalid PAN</p>}
              </div>
              <div>
                <Label className="tech-label">Aadhaar (12 digits) *</Label>
                <Input inputMode="numeric" maxLength={12} className="h-12 bg-zinc-50 mt-2" data-testid="input-aadhaar" {...register("aadhaar", { required: true, pattern: /^\d{12}$/ })} />
              </div>
            </div>

            {/* Training ID */}
            <div className="mt-6 border-t border-zinc-100 pt-6">
              <Label className="tech-label">Do you have Training ID? *</Label>
              <Controller
                control={control} name="has_training_id"
                render={({ field }) => (
                  <RadioGroup value={field.value} onValueChange={field.onChange} className="mt-3 flex gap-6">
                    <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="yes" data-testid="has-training-id-yes"/> Yes</label>
                    <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="no" data-testid="has-training-id-no"/> No</label>
                  </RadioGroup>
                )}
              />
              <AnimatePresence>
                {hasTrainingId === "yes" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                    className="mt-4 overflow-hidden"
                  >
                    <Label className="tech-label">Training ID *</Label>
                    <Input className="h-12 bg-zinc-50 mt-2 max-w-md" data-testid="input-training-id" {...register("training_id", { required: hasTrainingId === "yes" })} />
                    {errors.training_id && <p className="text-xs text-red-600 mt-1">Required</p>}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* BC/CSP */}
            <div className="mt-6 border-t border-zinc-100 pt-6">
              <Label className="tech-label">Are you an existing BC/CSP? *</Label>
              <Controller
                control={control} name="is_bc_csp"
                render={({ field }) => (
                  <RadioGroup value={field.value} onValueChange={field.onChange} className="mt-3 flex gap-6">
                    <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="yes" data-testid="is-bc-csp-yes"/> Yes</label>
                    <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="no" data-testid="is-bc-csp-no"/> No</label>
                  </RadioGroup>
                )}
              />
            </div>
          </section>

          {/* 3. Documents */}
          <section className="border border-zinc-200 rounded-lg p-8 bg-white">
            <div className="flex items-center gap-2 mb-1"><Building2 size={16} className="text-zinc-700"/><p className="tech-label">Step 03 — Documents</p></div>
            <h2 className="font-display text-2xl font-bold tracking-tight mb-6">Upload verified copies</h2>
            <div className="grid sm:grid-cols-2 gap-6">
              <FileField id="photo" label="Photograph" accept="image/*" hint="JPG/PNG · max 5MB" onFile={setFile("photo", IMG)} previewUrl={previews.photo?.url} testId="upload-photo" isPdf={false}/>
              <FileField id="signature" label="Signature" accept="image/*" hint="JPG/PNG · max 5MB" onFile={setFile("signature", IMG)} previewUrl={previews.signature?.url} testId="upload-signature" isPdf={false}/>
              <FileField id="pan_doc" label="PAN Card" accept="image/*,application/pdf" hint="JPG/PNG/PDF · max 5MB" onFile={setFile("pan", PDF_OR_IMG)} previewUrl={previews.pan?.url} testId="upload-pan" isPdf={previews.pan?.isPdf}/>
              <FileField id="aadhaar_doc" label="Aadhaar (PDF preferred)" accept="application/pdf,image/*" hint="PDF/JPG/PNG · max 5MB" onFile={setFile("aadhaar", PDF_OR_IMG)} previewUrl={previews.aadhaar?.url} testId="upload-aadhaar" isPdf={previews.aadhaar?.isPdf}/>
              <AnimatePresence>
                {isBcCsp === "yes" && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="sm:col-span-2">
                    <FileField id="bc_csp_doc" label="BC/CSP Proof" accept="image/*,application/pdf" hint="Existing BC/CSP proof · max 5MB" onFile={setFile("bc_csp", PDF_OR_IMG)} previewUrl={previews.bc_csp?.url} testId="upload-bc-csp" isPdf={previews.bc_csp?.isPdf}/>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </section>

          {/* 4. Declaration */}
          <section className="border border-zinc-200 rounded-lg p-8 bg-white">
            <p className="tech-label mb-4">Step 04 — Declaration</p>
            <div className="flex items-start gap-3">
              <Controller
                control={control}
                name="declaration"
                render={({ field }) => (
                  <Checkbox id="decl" checked={field.value} onCheckedChange={field.onChange} data-testid="declaration-checkbox" className="mt-1" />
                )}
              />
              <Label htmlFor="decl" className="text-sm leading-relaxed text-zinc-700">
                I hereby declare that all information provided above is true and correct to the best of my knowledge. I understand that any false statement may result in cancellation of my application.
              </Label>
            </div>
          </section>

          <WhatsAppInfo />

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
            <p className="text-xs text-zinc-500">Server calculates final fee based on selection — client cannot tamper.</p>
            <Button type="submit" size="lg" data-testid="submit-application-btn" disabled={submitting || !declaration} className="h-12 px-8 rounded-md bg-zinc-900 hover:bg-zinc-800 text-white font-semibold min-w-[220px]">
              {submitting ? "Submitting…" : (<><Check size={18} className="mr-2"/>Submit Application</>)}
            </Button>
          </div>
        </form>
      </main>
      <PublicFooter />
      <WhatsAppButton variant="floating" />
    </div>
  );
}
